import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  X,
  ExternalLink,
  DollarSign,
  Eye,
  MousePointer,
  Calendar,
  FileText,
  TrendingUp,
  Mail
} from 'lucide-react';
import Card from '@/components/ui/Card';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import { NewsletterPost } from '@/types/newsletter';
import { useDrawerNavigation } from '@/contexts/DrawerNavigationContext';

interface NewsletterPostsTableProps {
  posts: NewsletterPost[];
  loading?: boolean;
}

const NewsletterPostsTable: React.FC<NewsletterPostsTableProps> = ({ posts, loading = false }) => {
  const { openNewsletterPostInsights } = useDrawerNavigation();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'emv' | 'opens' | 'clicks' | 'date'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Format numbers with abbreviations
  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toLocaleString();
  };

  // Format EMV as currency
  const formatEMV = (value: number): string => {
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(2)}K`;
    }
    return `$${value.toFixed(2)}`;
  };

  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Filter and sort posts
  const filteredAndSortedPosts = useMemo(() => {
    let filtered = posts;

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = posts.filter(post =>
        post.title.toLowerCase().includes(search)
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'emv':
          comparison = a.stats.emv - b.stats.emv;
          break;
        case 'opens':
          comparison = a.stats.totalUniqueOpened - b.stats.totalUniqueOpened;
          break;
        case 'clicks':
          comparison = a.stats.totalClicked - b.stats.totalClicked;
          break;
        case 'date':
          comparison = new Date(a.publishDate).getTime() - new Date(b.publishDate).getTime();
          break;
      }

      return sortOrder === 'desc' ? -comparison : comparison;
    });

    return sorted;
  }, [posts, searchTerm, sortBy, sortOrder]);

  // Handle sort
  const handleSort = (column: typeof sortBy) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  // Open post insights in drawer
  const openPostDetailsDrawer = (post: NewsletterPost) => {
    openNewsletterPostInsights(post);
  };

  return (
    <div className="space-y-6">
      {/* Search */}
      <Card className="p-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <Input
                type="text"
                placeholder="Search newsletter posts..."
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                className="pl-10 pr-10"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          <div className="text-sm text-gray-500">
            {filteredAndSortedPosts.length} {filteredAndSortedPosts.length === 1 ? 'post' : 'posts'}
          </div>
        </div>
      </Card>

      {/* Posts Table */}
      <Card className="overflow-hidden relative">
        {loading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-avi-primary"></div>
              <span className="text-gray-900">Loading posts...</span>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Post Title
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('emv')}
                >
                  <div className="flex items-center space-x-1">
                    <span>EMV</span>
                    {sortBy === 'emv' && (
                      <TrendingUp className={`h-4 w-4 transition-transform ${
                        sortOrder === 'desc' ? 'rotate-0' : 'rotate-180'
                      }`} />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('opens')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Opens</span>
                    {sortBy === 'opens' && (
                      <TrendingUp className={`h-4 w-4 transition-transform ${
                        sortOrder === 'desc' ? 'rotate-0' : 'rotate-180'
                      }`} />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('clicks')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Clicks</span>
                    {sortBy === 'clicks' && (
                      <TrendingUp className={`h-4 w-4 transition-transform ${
                        sortOrder === 'desc' ? 'rotate-0' : 'rotate-180'
                      }`} />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rates
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('date')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Publish Date</span>
                    {sortBy === 'date' && (
                      <TrendingUp className={`h-4 w-4 transition-transform ${
                        sortOrder === 'desc' ? 'rotate-0' : 'rotate-180'
                      }`} />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAndSortedPosts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">
                      {searchTerm ? 'No posts match your search' : 'No posts found'}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredAndSortedPosts.map((post) => (
                  <motion.tr
                    key={post.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="cursor-pointer hover:bg-blue-50 transition-colors duration-150"
                    onClick={() => openPostDetailsDrawer(post)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-start">
                        <Mail className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-gray-900 line-clamp-2">
                            {post.title}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <DollarSign className="h-4 w-4 text-green-500 mr-1" />
                        <span className="text-sm font-semibold text-gray-900">
                          {formatEMV(post.stats.emv)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-500">
                        <Eye className="h-4 w-4 mr-1" />
                        {formatNumber(post.stats.totalUniqueOpened)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-500">
                        <MousePointer className="h-4 w-4 mr-1" />
                        {formatNumber(post.stats.totalClicked)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        <div className="flex items-center mb-1">
                          <span className="text-xs text-gray-400 mr-1">Open:</span>
                          <span>{post.stats.averageOpenRate.toFixed(1)}%</span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-xs text-gray-400 mr-1">Click:</span>
                          <span>{post.stats.averageClickRate.toFixed(1)}%</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {formatDate(post.publishDate)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-1">
                        <div className="relative group">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                              e.stopPropagation();
                              openPostDetailsDrawer(post);
                            }}
                            title="View details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <div className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                            View details
                          </div>
                        </div>
                        <div className="relative group">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                              e.stopPropagation();
                              // Open post URL in new tab if available
                              if (post.webUrl) {
                                window.open(post.webUrl, '_blank');
                              }
                            }}
                            title="Open in new tab"
                            disabled={!post.webUrl}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                          <div className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                            {post.webUrl ? 'Open post in new tab' : 'No URL available'}
                          </div>
                        </div>
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default NewsletterPostsTable;
